/**
 * macOS 오버레이 타이틀바의 크롬 줄.
 *
 * 웹에서 사이드바가 펼쳐지면 로고는 왼쪽, 검색과 접기는 오른쪽 끝이다.
 * macOS 창에서는 그 줄을 신호등이 쓰므로 로고를 그리지 않는다. 검색과 접기만 사이드바 오른쪽 끝에 둔다.
 * 전체 화면에서 신호등이 숨으면 웹과 같이 로고가 왼쪽으로 돌아온다.
 * 사이드바를 접으면 48px 레일이 남는다. 로고와 항목 아이콘은 그 자리에 두고, 다시 열기는 레일 하단에 둔다.
 * macOS에서 접힌 본문 맨 위는 신호등 오른쪽부터 시작한다.
 * 탭의 왼쪽 여백은 사이드바 너비와 함께 줄어, 접히는 동안 탭이 오른쪽으로 밀리지 않는다.
 * 숫자는 `styles/globals.css`의 `--chrome-*`와 `src-tauri/tauri.conf.json`의
 * `trafficLightPosition`과 같다. 한곳을 바꾸면 테스트가 나머지를 잡는다.
 */
import { isDesktop, platform } from "./runtime";

/** 페이지 헤더와 사이드바 헤더가 공유하는 창 맨 위 줄. */
export const CHROME_ROW_PX = 44;
/** 신호등 지름. macOS 표준. */
export const TRAFFIC_LIGHT_DIAMETER = 12;
/** 신호등 묶음. 12 + 간격 8 + 12 + 간격 8 + 12. */
export const TRAFFIC_CLUSTER_PX = 52;
/** 창 왼쪽에서 신호등까지. */
export const TRAFFIC_LIGHT_X = 14;
/** 신호등 묶음과 숨기기 버튼 사이. 신호등끼리의 간격(8px)과 같다. */
export const TRAFFIC_AFTER_GAP_PX = 8;
/** 창 왼쪽에서 첫 컨트롤·제목까지. */
export const CHROME_SAFE_LEFT_PX = TRAFFIC_LIGHT_X + TRAFFIC_CLUSTER_PX + TRAFFIC_AFTER_GAP_PX;
/** 접힌 탐색 레일. `AppNav`의 `RAIL_WIDTH`와 같다. */
export const NAV_RAIL_PX = 48;
/** 사이드바 숨기기 버튼 한 변. */
export const CHROME_TOGGLE_PX = 28;
/** 숨기기 버튼 오른쪽 간격. 접힌 본문 제목과의 거리. */
export const CHROME_TOGGLE_GAP_PX = 12;
/** 펼친 사이드바에서 검색과 접기 버튼 사이. */
export const CHROME_NAV_GAP_PX = 4;
/** 접힌 사이드바의 숨기기 버튼 왼쪽. macOS는 신호등 오른쪽, 그 밖에는 레일 한가운데. */
export const CHROME_TOGGLE_LEFT_MACOS_PX = CHROME_SAFE_LEFT_PX;
export const CHROME_TOGGLE_LEFT_DEFAULT_PX = (NAV_RAIL_PX - CHROME_TOGGLE_PX) / 2;
/** macOS에서 접힌 본문 맨 위 줄 첫 요소의 창 기준 왼쪽. 신호등 오른쪽이다. */
export const CHROME_CONTENT_LEFT_PX = CHROME_SAFE_LEFT_PX;
/** 웹·Windows에서 접힌 본문 맨 위 줄 첫 요소의 왼쪽. */
export const CHROME_CONTENT_LEFT_DEFAULT_PX = CHROME_TOGGLE_LEFT_DEFAULT_PX + CHROME_TOGGLE_PX + CHROME_TOGGLE_GAP_PX;
/** 접혔을 때 본문 맨 위 줄이 레일 오른쪽에서 더 비우는 너비(macOS). */
export const COLLAPSED_CONTENT_INSET_PX = CHROME_CONTENT_LEFT_PX - NAV_RAIL_PX;
/** 펼친 사이드바 크롬의 왼쪽 여백. 웹은 헤더 여백, macOS 창은 신호등 오른쪽이다. 그 창에서는 로고를 숨긴다. */
export const CHROME_NAV_LEFT_MACOS_PX = CHROME_SAFE_LEFT_PX;
export const CHROME_NAV_LEFT_DEFAULT_PX = 8;
/** 신호등 위쪽. 크롬 줄 가운데에 12px 버튼을 둔다. */
export const TRAFFIC_LIGHT_Y = (CHROME_ROW_PX - TRAFFIC_LIGHT_DIAMETER) / 2;
/**
 * 숨기기 버튼 위쪽.
 * macOS는 신호등을 크롬 줄 한가운데보다 위에 그린다. 버튼 중심을 그 높이에 둔다.
 */
export const CHROME_TOGGLE_TOP_PX = 2;
/** 접을 때 검색·접기가 제자리에서 사라지는 시간. 로고는 남는다. */
export const CHROME_HIDE_MS = 40;
/** 사이드바 너비·안쪽 줄 애니메이션. 접고 펴는 동작은 이 길이로 같이 끝난다. */
export const NAV_WIDTH_MS = 80;
/** 접힐 때 너비 시작을 늦추지 않는다. 검색·접기는 너비와 같이 사라진다. */
export const NAV_WIDTH_DELAY_MS = 0;

/** 첫 페인트 전에 호출한다. 웹에서는 아무 것도 하지 않는다. */
export function markDesktopDocument(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (!isDesktop()) {
    delete root.dataset.desktop;
    return;
  }
  root.dataset.desktop = platform();
}
